import React, { useEffect, useState } from 'react';
import { Heart, Droplets, Stethoscope, BookOpen, TrendingUp, CheckCircle } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { Link } from 'react-router-dom';
function CountUp({
  end,
  duration = 2000,
  prefix = '',
  suffix = ''
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  return <span data-id="element-207">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export function CharityPage() {
  const currentProjects = [{
    icon: Droplets,
    title: 'Clean Water Initiative',
    desc: 'Installing water filtration plants and hand pumps in rural villages across KPK to prevent waterborne diseases and improve community health.',
    image: 'https://images.unsplash.com/photo-1541844053589-346841d0b34c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    status: 'Active',
    impact: '5 villages, 2,000+ people'
  }, {
    icon: Stethoscope,
    title: 'Free Medical Camps',
    desc: 'Providing free consultations, medicines, and minor surgeries to underserved populations in remote areas of Pakistan.',
    image: 'https://images.unsplash.com/photo-1666214280557-f1b5022eb634?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    status: 'Ongoing',
    impact: 'Quarterly camps, 500+ patients per camp'
  }, {
    icon: BookOpen,
    title: 'Medical Education Fund',
    desc: 'Scholarships for deserving medical students from KPK who cannot afford tuition fees, supporting the next generation of doctors.',
    image: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    status: 'Active',
    impact: '15 students supported annually'
  }];
  const pastAchievements = [{
    year: '2023',
    title: 'Flood Relief Mission',
    desc: 'Emergency medical aid and supplies delivered to flood-affected regions in KPK.',
    impact: '3,000+ families assisted'
  }, {
    year: '2023',
    title: 'Eye Camp Success',
    desc: 'Over 200 cataract surgeries performed for underserved communities.',
    impact: '200+ surgeries completed'
  }, {
    year: '2022',
    title: 'COVID-19 Response',
    desc: 'PPE distribution and medical support during the pandemic.',
    impact: '50+ healthcare facilities supported'
  }, {
    year: '2021',
    title: 'Education Scholarships',
    desc: 'Launched scholarship program for medical students from low-income families.',
    impact: '25 students funded'
  }];
  return <>
      <SEO title="Charity Work" description="Our humanitarian projects and charitable initiatives supporting healthcare and communities in Pakistan." data-id="element-208" />

      {/* Hero */}
      <div className="relative bg-primary-900 text-white py-24 overflow-hidden" data-id="element-209">
        <div className="absolute inset-0" data-id="element-210">
          <img src="https://images.unsplash.com/photo-1593113598332-cd288d649433?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Humanitarian aid and food distribution" className="h-full w-full object-cover opacity-10" data-id="element-211" />
        </div>
        <div className="relative container mx-auto px-4 text-center" data-id="element-212">
          <FadeIn data-id="element-213">
            <Heart className="h-12 w-12 mx-auto mb-6 text-red-400" data-id="element-214" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-215">
              Serving Humanity
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto leading-relaxed" data-id="element-216">
              We believe healthcare is a fundamental human right. Our charity
              work brings relief, hope, and healing to those who need it most.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Impact Counter */}
      <Section className="relative -mt-16 pt-0 z-10" data-id="element-217">
        <FadeIn data-id="element-218">
          <Card className="border-none shadow-2xl bg-gradient-to-br from-green-50 to-white max-w-2xl mx-auto" data-id="element-219">
            <CardContent className="p-8 md:p-12 text-center" data-id="element-220">
              <div className="text-5xl md:text-6xl font-bold text-green-600 mb-3" data-id="element-221">
                <CountUp end={20000} prefix="£" data-id="element-222" />
              </div>
              <div className="text-lg font-medium text-gray-700 mb-2" data-id="element-223">
                Total Funds Raised
              </div>
              <p className="text-sm text-gray-600" data-id="element-224">
                100% of donations go directly to charitable projects
              </p>
            </CardContent>
          </Card>
        </FadeIn>
      </Section>

      {/* Current Activities */}
      <Section data-id="element-225">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-226">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-227">
            Current Charitable Activities
          </h2>
          <p className="text-gray-600 text-lg" data-id="element-228">
            Through the generous support of our members and donors, we run
            several ongoing projects focused on health, education, and community
            development.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-229">
          {currentProjects.map((project, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-230">
              <Card className="h-full overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow" data-id="element-231">
                <div className="h-48 overflow-hidden relative" data-id="element-232">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-233" />
                  <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-234">
                    {project.status}
                  </div>
                </div>
                <CardContent className="p-6" data-id="element-235">
                  <div className="h-12 w-12 mx-auto -mt-12 bg-white rounded-full flex items-center justify-center shadow-md mb-4 relative z-10" data-id="element-236">
                    <project.icon className="h-6 w-6 text-primary-600" data-id="element-237" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center" data-id="element-238">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-239">
                    {project.desc}
                  </p>
                  <div className="pt-4 border-t border-gray-100" data-id="element-240">
                    <div className="flex items-center justify-center text-sm text-gray-700" data-id="element-241">
                      <TrendingUp className="h-4 w-4 mr-2 text-green-600" data-id="element-242" />
                      <span className="font-medium" data-id="element-243">{project.impact}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Past Achievements */}
      <Section background="gray" data-id="element-244">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-245">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-246">
            Past Achievements
          </h2>
          <p className="text-gray-600" data-id="element-247">
            A track record of meaningful impact and lives changed through
            dedicated humanitarian service.
          </p>
        </div>

        <div className="max-w-4xl mx-auto" data-id="element-248">
          <div className="space-y-6" data-id="element-249">
            {pastAchievements.map((achievement, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-250">
                <Card className="hover:shadow-md transition-shadow" data-id="element-251">
                  <CardContent className="p-6" data-id="element-252">
                    <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-253">
                      <div className="flex-shrink-0" data-id="element-254">
                        <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center" data-id="element-255">
                          <span className="text-lg font-bold" data-id="element-256">
                            {achievement.year}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1" data-id="element-257">
                        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-258">
                          {achievement.title}
                        </h3>
                        <p className="text-gray-600 mb-2" data-id="element-259">{achievement.desc}</p>
                        <div className="flex items-center text-sm text-green-700 font-medium" data-id="element-260">
                          <CheckCircle className="h-4 w-4 mr-2" data-id="element-261" />
                          {achievement.impact}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Future Plans */}
      <Section data-id="element-262">
        <div className="max-w-4xl mx-auto" data-id="element-263">
          <div className="text-center mb-12" data-id="element-264">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-265">
              Future Plans
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-266">
              Our vision for expanding humanitarian impact in the years ahead.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-267">
            {[{
            title: 'Mobile Medical Units',
            desc: 'Deploy fully-equipped mobile clinics to reach the most remote villages in KPK.',
            timeline: '2024-2025'
          }, {
            title: 'Telemedicine Program',
            desc: 'Connect UK-based specialists with patients in Pakistan through virtual consultations.',
            timeline: '2024'
          }, {
            title: "Women's Health Initiative",
            desc: 'Focused programs addressing maternal and reproductive health in underserved areas.',
            timeline: '2025'
          }, {
            title: 'Disaster Response Fund',
            desc: 'Establish a rapid-response fund for natural disasters and humanitarian emergencies.',
            timeline: 'Ongoing'
          }].map((plan, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-268">
                <Card className="h-full border-primary-100" data-id="element-269">
                  <CardHeader data-id="element-270">
                    <CardTitle className="text-lg" data-id="element-271">{plan.title}</CardTitle>
                    <CardDescription className="text-xs font-medium text-accent-600" data-id="element-272">
                      Target: {plan.timeline}
                    </CardDescription>
                  </CardHeader>
                  <CardContent data-id="element-273">
                    <p className="text-gray-600 text-sm" data-id="element-274">{plan.desc}</p>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Donation CTA */}
      <Section background="primary" className="text-center" data-id="element-275">
        <FadeIn data-id="element-276">
          <Heart className="h-12 w-12 mx-auto mb-6 text-red-400" data-id="element-277" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-white" data-id="element-278">
            Make a Difference Today
          </h2>
          <p className="text-primary-100 mb-8 max-w-2xl mx-auto text-lg leading-relaxed" data-id="element-279">
            Your contribution saves lives. 100% of your donation goes directly
            to our charitable projects. We maintain a strict zero-admin-fee
            policy for all donations.
          </p>

          <div className="bg-white/10 backdrop-blur rounded-xl p-8 max-w-md mx-auto mb-8" data-id="element-280">
            <h3 className="font-bold text-white mb-4 text-lg" data-id="element-281">
              Bank Transfer Details
            </h3>
            <div className="space-y-2 text-left text-sm text-primary-50" data-id="element-282">
              <div className="flex justify-between border-b border-white/20 pb-2" data-id="element-283">
                <span data-id="element-284">Bank Name:</span>
                <span className="font-medium text-white" data-id="element-285">Barclays Bank</span>
              </div>
              <div className="flex justify-between border-b border-white/20 pb-2" data-id="element-286">
                <span data-id="element-287">Account Name:</span>
                <span className="font-medium text-white" data-id="element-288">KMA UK Charity</span>
              </div>
              <div className="flex justify-between border-b border-white/20 pb-2" data-id="element-289">
                <span data-id="element-290">Sort Code:</span>
                <span className="font-medium text-white" data-id="element-291">20-00-00</span>
              </div>
              <div className="flex justify-between pb-2" data-id="element-292">
                <span data-id="element-293">Account No:</span>
                <span className="font-medium text-white" data-id="element-294">12345678</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-295">
            <Button size="lg" variant="secondary" data-id="element-296">
              <Heart className="mr-2 h-5 w-5" data-id="element-297" />
              Donate Now
            </Button>
            <Link to="/contact" data-id="element-298">
              <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10 hover:text-white" data-id="element-299">
                Contact Us About Donations
              </Button>
            </Link>
          </div>
        </FadeIn>
      </Section>
    </>;
}